import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTriageAssessment } from '../hooks/useApi';
import { TriageResult } from '../types/api';
import ApiError from '../components/ApiError';
import { AlertTriangle, ChevronRight } from 'lucide-react';

const quickSymptoms = ['Headache', 'Fever', 'Cough', 'Chest pain', 'Nausea', 'Dizziness'];

const severityColors: Record<string, string> = {
  emergency: 'bg-red-600',
  urgent: 'bg-accent',
  routine: 'bg-primary',
};

const recommendationLabels: Record<string, { text: string; color: string }> = {
  call_911: { text: 'Call 911 Immediately', color: 'text-red-600' },
  go_to_er: { text: 'Visit Emergency Room', color: 'text-red-600' },
  visit_clinic: { text: 'Visit a Clinic', color: 'text-accent' },
  self_care: { text: 'Self-Care at Home', color: 'text-primary' },
};

function TriageResultCard({ result, onReset }: { result: TriageResult; onReset: () => void }) {
  const rec = recommendationLabels[result.recommendation] || { text: result.recommendation, color: 'text-gray-700' };

  return (
    <div className="card overflow-hidden">
      <div className={`${severityColors[result.severity] || 'bg-gray-600'} text-white px-6 py-3 -mx-6 -mt-6 mb-4`}>
        <p className="font-bold uppercase tracking-wider text-sm">
          {result.severity} — {rec.text}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <p className="label">Matched Symptom</p>
          <p className="text-navy font-medium">{result.matched_symptom}</p>
        </div>

        <div className="bg-cream rounded-lg p-4">
          <p className="label">Guidance</p>
          <p className="text-gray-600 text-sm leading-relaxed">{result.guidance}</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-xs font-medium">{result.disclaimer}</p>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onReset} className="btn btn-outline flex-1">
            Check Different Symptoms
          </button>
          <Link to="/clinics" className="btn btn-primary flex-1">
            Find a Clinic
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function TriagePage() {
  const [symptoms, setSymptoms] = useState('');
  const [result, setResult] = useState<TriageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mutation = useTriageAssessment();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!symptoms.trim()) return;
    setError(null);
    setResult(null);

    try {
      const data = await mutation.mutateAsync(symptoms);
      setResult(data);
    } catch {
      setError('Unable to process symptoms. Please try again or call 911 if this is an emergency.');
    }
  };

  const addSymptom = (symptom: string) => {
    setSymptoms((prev) => (prev ? `${prev}, ${symptom.toLowerCase()}` : symptom.toLowerCase()));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-navy font-medium">Symptom Checker</span>
      </div>

      <h1 className="text-3xl font-extrabold text-navy">Symptom Checker</h1>
      <p className="text-gray-400 mt-2 mb-6">Describe your symptoms for guidance on next steps</p>

      {/* Disclaimer */}
      <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <p className="text-yellow-800 text-sm">
          This tool provides general guidance only and is not a medical diagnosis.
          If you are experiencing a medical emergency, call <strong>911</strong> immediately.
        </p>
      </div>

      {!result && !error && (
        <form onSubmit={handleSubmit} className="card">
          <label className="label">Describe your symptoms</label>
          <textarea
            value={symptoms}
            onChange={(e) => setSymptoms(e.target.value)}
            placeholder="e.g., headache, fever, cough..."
            rows={4}
            className="input resize-none"
          />

          <div className="flex flex-wrap gap-2 mt-3">
            {quickSymptoms.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addSymptom(s)}
                className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-full text-gray-600 hover:border-primary hover:text-primary transition-colors"
              >
                + {s}
              </button>
            ))}
          </div>

          <button
            type="submit"
            disabled={!symptoms.trim() || mutation.isPending}
            className="btn btn-primary w-full mt-6"
          >
            {mutation.isPending ? 'Analyzing...' : 'Check Symptoms'}
          </button>
        </form>
      )}

      {result && <TriageResultCard result={result} onReset={() => { setResult(null); setSymptoms(''); }} />}

      {error && (
        <div>
          <ApiError message={error} showEmergencyInfo onRetry={() => { setError(null); setResult(null); }} />
        </div>
      )}
    </div>
  );
}
